// Discover page - Music discovery and feedback
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createPageUrl, getCsrfToken } from "@/utils";
import { Bookmark, BookmarkCheck, Share2, MessageSquare } from "lucide-react";
import SendMessageButton from "@/components/SendMessageButton";
import btnPlay from "@/assets/btn-play.png";
import btnGood from "@/assets/btn-good.png";
import btnBad from "@/assets/btn-bad.png";
import crowRight from "@/assets/crow-right.png";
import crowSkullLeft from "@/assets/crow-skull-left.png";
import { Button } from "@/components/ui/button";
import { TooltipButton } from "@/components/ui/TooltipButton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { usePlayer } from "@/contexts/PlayerContext";
import CommentSection from "@/components/CommentSection";
import { WinnerBadge } from "@/components/WinnerBadge";
import { LeaderboardBadge } from "@/components/LeaderboardBadge";
import { useLeaderboardPosition } from "@/hooks/useLeaderboardPosition";
import { hapticMedium, hapticSuccess, hapticError } from "@/utils/haptic";
import ShareDialog from "@/components/ShareDialog";
import { getDefaultCoverImage } from "@/utils/defaultCoverImage";
import { DiscoverSkeleton } from "@/components/ui/skeleton-card";

// Fisher-Yates shuffle algorithm for true randomization
// Moved outside component to prevent initialization issues
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Helper component to show leaderboard badge for artist
function ArtistLeaderboardBadge({ artistId }) {
  const { data: position } = useLeaderboardPosition(artistId);
  return position ? <LeaderboardBadge position={position} /> : null;
}
ArtistLeaderboardBadge.propTypes = {
  artistId: PropTypes.string
};

export default function Discover() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const isAdminLoadedRef = useRef(false); // Track if admin status was loaded to prevent re-renders
  const [feedbackText, setFeedbackText] = useState("");
  const [rating, setRating] = useState(5); // Default to 5 (no star UI needed)
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasPraised, setHasPraised] = useState(false);
  const [hasScathed, setHasScathed] = useState(false);
  const [artistBadgeStatus, setArtistBadgeStatus] = useState(false);
  const [trackCompleted, setTrackCompleted] = useState(false); // Track if 100% completed
  const [reviewDisabled, setReviewDisabled] = useState(false); // Keep enabled; state retained for UI compatibility
  const [requiredCompletionThreshold, setRequiredCompletionThreshold] = useState(75); // Default 75% for <5min tracks
  const lastProgressMilestoneRef = useRef(0); // Track last milestone (0, 10, 20, etc.)
  const lastProgressUpdateTimeRef = useRef(0); // Track timestamp of last DB update
  const [isSaved, setIsSaved] = useState(false); // Track if current track is saved
  const [hasReviewed, setHasReviewed] = useState(false); // Track if user has already reviewed
  const [artistBio, setArtistBio] = useState(null); // Artist bio from profile
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [shuffledTracks, setShuffledTracks] = useState([]);
  const [listenTimeSeconds, setListenTimeSeconds] = useState(0); // Track actual listen time in seconds
  
  // Countdown timer state
  const [countdown, setCountdown] = useState(null); // null = not counting, number = seconds remaining
  const countdownRef = useRef(null); // Interval reference
  
  const {
    isPlaying,
    progress,
    togglePlayPause,
    skipToNext,
    skipToPrevious,
    playTrack,
    currentTrack: playerCurrentTrack,
    currentTrackIndex,
    trackDuration,
    registerProgressCallback,
    audioRef,
    setProgress,
    trackEnded,
    setTrackEnded,
    registerOnTrackEnd,
    clearFailedUrls,
  } = usePlayer();
  
  // Track audio error state to auto-skip
  const [audioErrorOccurred, setAudioErrorOccurred] = useState(false);

  // Derived track list & selection, computed after shuffledTracks is declared
  const tracks = useMemo(
    () => (shuffledTracks.length > 0 ? shuffledTracks : []),
    [shuffledTracks]
  );

  const currentTrack = useMemo(
    () => playerCurrentTrack || (tracks.length > 0 ? tracks[0] : null),
    [playerCurrentTrack, tracks]
  );

  useEffect(() => {
    document.title = "Discover - Soundope";
    loadUser();
  }, []);

  const loadUser = async () => {
    // Skip if already loaded to prevent infinite re-renders
    if (isAdminLoadedRef.current) return;
    
    try {
      const { data: sessionResult } = await supabase.auth.getSession();
      const session = sessionResult?.session;
      if (!session || !session.user) {
        setCurrentUser(null);
        isAdminLoadedRef.current = true; // Mark as loaded even if no user
        return;
      }

      const { user } = session;
      const { data: profile, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();
      
      if (error) {
        console.error("Error loading profile:", error);
        isAdminLoadedRef.current = true;
        return;
      }
      
      setCurrentUser(profile);

      // Check if user is admin - fetch from Supabase
      const { data: roles, error: rolesError } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);
      
      console.log("[Discover] Admin check - user.id:", user.id, "roles response:", roles, "error:", rolesError);
      const hasAdminAccess = Array.isArray(roles) && roles.some(r => r.role === 'admin');
      console.log("[Discover] hasAdminAccess:", hasAdminAccess);
      
      // Set isAdmin only once to prevent re-render loops
      setIsAdmin(hasAdminAccess);
      isAdminLoadedRef.current = true;
    } catch (error) {
      console.error("Error loading user:", error);
      isAdminLoadedRef.current = true;
    }
  };

  // Fetch tracks the user has already reviewed (to exclude from discovery)
  const { data: reviewedTrackIds = [], isLoading: isLoadingReviewedTracks } = useQuery({
    queryKey: ["reviewed-tracks", currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return [];
      const { data, error } = await supabase
        .from("feedback")
        .select("track_id")
        .eq("reviewer_id", currentUser.id);
      
      if (error) throw error;
      return data?.map(r => r.track_id) || [];
    },
    enabled: !!currentUser?.id,
    staleTime: 30000,
  });

  // Fetch tracks the user has played (to deprioritize skipped tracks)
  // Note: track_plays table doesn't exist in Supabase, using votes as proxy
  const { data: playedTrackIds = [], isLoading: isLoadingPlayedTracks } = useQuery({
    queryKey: ["played-tracks", currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return [];
      // Use votes table as proxy for played tracks since track_plays doesn't exist
      const { data, error } = await supabase
        .from("votes")
        .select("track_id")
        .eq("voter_id", currentUser.id);
      
      if (error) throw error;
      return data?.map(p => p.track_id) || [];
    },
    enabled: !!currentUser?.id,
    staleTime: 30000,
  });

  // Track if we've shown the error toast (to avoid spamming)
  const errorToastShownRef = useRef(false);
  
  // Removed unused tracksError to fix unused variable lint error
  const { data: rawTracks = [], isLoading, refetch, isError } = useQuery({
    queryKey: ["discover-tracks"],
    queryFn: async () => {
      console.log("[Discover] Fetching tracks from database...");
      try {
        const { data, error } = await supabase
          .from("tracks")
          .select("*")
          .eq("is_active", true)
          .order("created_at", { ascending: false });
        
        if (error) {
          console.error("[Discover] Error fetching tracks:", error);
          throw error;
        }
        
        console.log(`[Discover] Successfully fetched ${data?.length || 0} tracks`);
        errorToastShownRef.current = false; // Reset on success
        return data || [];
      } catch (err) {
        console.error("[Discover] Fetch error:", err);
        // Only show toast once per error cycle
        if (!errorToastShownRef.current) {
          errorToastShownRef.current = true;
          toast.error("Failed to load tracks. Please try again.");
        }
        throw err;
      }
    },
    staleTime: 30000, // Cache for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    retry: 3, // Retry failed requests 3 times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff
  });

  // Shuffle tracks: prioritize newest unheard tracks with weighted probability, older tracks deprecate
  // Use a ref to track if shuffle was already done this session to prevent infinite loops
  const shuffleCompletedRef = useRef(false);
  const lastAdminStateRef = useRef(null); // Track admin state to re-shuffle when it changes
  
  useEffect(() => {
    // Admins have NO feedback limit - they see all tracks
    const FEEDBACK_LIMIT = 500; // Only applies to non-admins
    
    // Only shuffle if we have tracks and haven't already shuffled
    // Also wait for admin status to be loaded if there's a user
    const isReady = rawTracks.length > 0 && 
      (!currentUser?.id || (!isLoadingReviewedTracks && !isLoadingPlayedTracks && isAdminLoadedRef.current));
    
    // Re-shuffle if admin status changed (e.g., loaded after initial shuffle)
    const adminStateChanged = lastAdminStateRef.current !== null && lastAdminStateRef.current !== isAdmin;
    
    // Don't re-shuffle if already done (prevents infinite loops) - UNLESS admin state changed
    if (!isReady || (shuffleCompletedRef.current && !adminStateChanged)) return;
    
    const reviewedSet = new Set(reviewedTrackIds);
    
    // Helper to check if track has an audio URL (player handles validation)
    const hasAudioUrl = (track) => {
      return !!(track?.audio_url && track.audio_url.trim());
    };
    
    // Filter out reviewed tracks and by feedback limit unless admin
    const availableTracks = rawTracks.filter(track => {
      // Filter out tracks without any audio URL
      if (!hasAudioUrl(track)) return false;
      // ADMINS SEE ALL TRACKS - no limits whatsoever
      if (isAdmin) return true;
      // Filter out tracks the user has already reviewed (non-admins only)
      if (reviewedSet.has(track.id)) return false;
      // For non-admins, filter out tracks that have reached the feedback limit (HARD CUTOFF)
      const totalFeedback = (track.praise_count || 0) + (track.neutral_count || 0) + (track.scathe_count || 0);
      return totalFeedback < FEEDBACK_LIMIT;
    });
    
    console.log(`[Discover] Shuffling ${rawTracks.length} tracks -> ${availableTracks.length} available (TRUE RANDOM) isAdmin: ${isAdmin}`);
    
    // TRUE RANDOM SHUFFLE using Fisher-Yates algorithm - no weighting, completely random
    const shuffled = shuffleArray(availableTracks);
    
    setShuffledTracks(shuffled);
    shuffleCompletedRef.current = true;
    lastAdminStateRef.current = isAdmin;
  }, [rawTracks, isLoadingReviewedTracks, isLoadingPlayedTracks, currentUser?.id, reviewedTrackIds, isAdmin]);

  // Initialize player when shuffled tracks are loaded
  // Only load track if no current track is already set - DO NOT auto-play (user must press play)
  useEffect(() => {
    if (shuffledTracks.length > 0 && !playerCurrentTrack) {
      // Load track but do NOT start playing (startPlaying = false)
      playTrack(shuffledTracks[0], shuffledTracks, 0, false, false);
    }
  }, [shuffledTracks, playTrack, playerCurrentTrack]);

  // Track plays when track changes - update total_listens on tracks table
  useEffect(() => {
    const recordPlay = async () => {
      if (!playerCurrentTrack?.id || !currentUser?.id) return;
      
      try {
        // Update total_listens directly in Supabase
        const { data: currentTrackData, error: fetchError } = await supabase
          .from('tracks')
          .select('total_listens')
          .eq('id', playerCurrentTrack.id)
          .single();
        
        if (fetchError) {
          console.error("Error fetching track listens:", fetchError);
          return;
        }
        
        // Update total_listens
        const { error: updateError } = await supabase
          .from('tracks')
          .update({ total_listens: (currentTrackData?.total_listens || 0) + 1 })
          .eq('id', playerCurrentTrack.id);
        
        if (updateError) {
          console.error("Error updating listens:", updateError);
        }
      } catch (error) {
        console.error("Error recording play:", error);
      }
    };

    recordPlay();
  }, [playerCurrentTrack, currentUser]);

  // Set required completion threshold to 70% (fixed)
  useEffect(() => {
    setRequiredCompletionThreshold(70);
  }, []);

  // Track progress updates - just for local state
  useEffect(() => {
    if (!currentTrack?.id || !currentUser?.id || progress < 0) return;

    // Check for 100% completion
    if (progress >= 100 && !trackCompleted && currentTrack?.id) {
      setTrackCompleted(true);
    }
  }, [progress, trackCompleted, currentTrack, currentUser]);

  // Register progress callback for milestone tracking and listen time
  useEffect(() => {
    const cleanup = registerProgressCallback((progressPercent, milestone, currentTime, duration) => {
      // Track actual listen time in seconds
      if (currentTime && Number.isFinite(currentTime)) {
        setListenTimeSeconds(currentTime);
      }
    });
    return cleanup;
  }, [registerProgressCallback]);

  // Reset listen time when track changes
  useEffect(() => {
    setListenTimeSeconds(0);
  }, [playerCurrentTrack?.id]);

  // Countdown timer logic - starts 25 second countdown when track ends
  const startCountdown = useCallback(() => {
    // Clear any existing countdown
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    
    setCountdown(13);
    
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
          // Auto-skip to next track
          skipToNext();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  }, [skipToNext]);

  const cancelCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setCountdown(null);
    setTrackEnded(false);
  }, [setTrackEnded]);

  // Register track end callback to start countdown
  useEffect(() => {
    const cleanup = registerOnTrackEnd(() => {
      startCountdown();
    });
    return cleanup;
  }, [registerOnTrackEnd, startCountdown]);

  // Cancel countdown when track changes
  useEffect(() => {
    cancelCountdown();
  }, [playerCurrentTrack?.id, cancelCountdown]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);

  // Listen for audio errors and auto-skip to next track
  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio) return;
    
    const handleAudioError = () => {
      setAudioErrorOccurred(true);
      // Auto-skip to next track after 2 seconds to give user time to see the error
      setTimeout(() => {
        if (shuffledTracks.length > 1) {
          toast.info("Skipping to next track...");
          skipToNext();
        }
        setAudioErrorOccurred(false);
      }, 2000);
    };
    
    audio.addEventListener('error', handleAudioError);
    return () => {
      audio.removeEventListener('error', handleAudioError);
    };
  }, [audioRef, skipToNext, shuffledTracks.length]);

  // Reset audio error state when track changes
  useEffect(() => {
    setAudioErrorOccurred(false);
  }, [playerCurrentTrack?.id]);

  // Allow seeking within the current track (for testing / fast-forward)
  const handleSeek = (event) => {
    if (!audioRef?.current) return;
    const newProgress = Number(event.target.value);
    if (Number.isNaN(newProgress)) return;

    const audio = audioRef.current;
    const duration = audio.duration || trackDuration;
    if (!duration || !Number.isFinite(duration) || duration <= 0) return;

    const newTime = (newProgress / 100) * duration;
    audio.currentTime = newTime;
    setProgress(newProgress);
  };

  // Keep review enabled always - no typing restrictions
  useEffect(() => {
    setReviewDisabled(false);
  }, [trackCompleted]);

  const countWords = (text) => {
    if (!text) return 0;
    // Remove emojis and count words
    const withoutEmojis = text.replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu, '');
    return withoutEmojis.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const handleSubmitFeedback = async () => {
    console.log("[Discover] handleSubmitFeedback called", { 
      hasUser: !!currentUser, 
      hasTrack: !!currentTrack,
      rating,
      wordCount: countWords(feedbackText),
      progress 
    });

    if (!currentUser || !currentTrack) {
      console.error("[Discover] Missing user or track");
      toast.error("Please log in to submit a review");
      return;
    }
    
    if (!currentTrack?.id || !currentUser?.id) {
      console.error("[Discover] Missing track or user ID");
      toast.error("Missing required data");
      return;
    }

    // Validate rating
    // Rating is now auto-set to 5, no validation needed

    const words = countWords(feedbackText);
    
    // Check minimum word count requirement (20 words minimum)
    if (words < 20) {
      hapticError();
      toast.error("Review must be at least 20 words long");
      return;
    }
    
    // Use progress state directly for completion validation
    let actualCompletion = progress;

    // Check if completion meets required threshold: 70% OR 3 minutes (180 seconds) - admins bypass this
    const hasListenedEnough = actualCompletion >= 70 || listenTimeSeconds >= 180;
    if (!hasListenedEnough && !isAdmin) {
      hapticError();
      toast.error(`Please listen to at least 70% of the track or 3 minutes (current: ${Math.round(actualCompletion)}%, ${Math.floor(listenTimeSeconds / 60)}:${String(Math.floor(listenTimeSeconds % 60)).padStart(2, '0')} listened)`);
      return;
    }

    console.log("[Discover] All checks passed, submitting review...");
    
    setIsSubmitting(true);
    try {
      // Get track duration (use from PlayerContext or track metadata)
      const trackDurationSeconds = trackDuration || currentTrack.duration || null;

      console.log("[Discover] Submitting review to Supabase", {
        track_id: currentTrack.id,
        word_count: words,
        completion_percentage: actualCompletion,
        track_duration_seconds: trackDurationSeconds
      });

      // Determine feedback type based on rating or default to neutral
      const feedbackType = rating >= 4 ? 'praise' : rating <= 2 ? 'scathe' : 'neutral';

      // Insert feedback directly to Supabase
      const { data: feedbackData, error: feedbackError } = await supabase
        .from('feedback')
        .insert({
          track_id: currentTrack.id,
          reviewer_id: currentUser.id,
          feedback_type: feedbackType,
          comment: feedbackText
        })
        .select()
        .single();

      if (feedbackError) {
        console.error("[Discover] Feedback insert error:", feedbackError);
        throw new Error(feedbackError.message || "Failed to submit feedback");
      }

      console.log("[Discover] Feedback submitted successfully:", feedbackData);

      // Update track counts
      const updateField = feedbackType === 'praise' ? 'praise_count' : 
                         feedbackType === 'scathe' ? 'scathe_count' : 'neutral_count';
      
      const { data: trackData } = await supabase
        .from('tracks')
        .select(updateField)
        .eq('id', currentTrack.id)
        .single();
      
      await supabase
        .from('tracks')
        .update({ [updateField]: (trackData?.[updateField] || 0) + 1 })
        .eq('id', currentTrack.id);

      // Award review credit - add 1 cent to earned_credits
      const { data: profileData } = await supabase
        .from('profiles')
        .select('earned_credits')
        .eq('id', currentUser.id)
        .single();
      
      const newBalance = (profileData?.earned_credits || 0) + 1;
      
      await supabase
        .from('profiles')
        .update({ earned_credits: newBalance })
        .eq('id', currentUser.id);

      // Dispatch event to update header balance immediately
      window.dispatchEvent(new CustomEvent('earnedCreditsUpdated', { 
        detail: { newBalance: newBalance } 
      }));
      
      hapticSuccess();
      const balanceInDollars = (newBalance / 100).toFixed(2);
      toast.success(`Review submitted! You earned $0.01! (Balance: $${balanceInDollars})`);
      
      // Refresh user data to show updated credits
      loadUser();

      setFeedbackText("");
      setHasReviewed(true);
      
      // Invalidate reviewed-tracks query so this track won't appear again
      queryClient.invalidateQueries({ queryKey: ["reviewed-tracks", currentUser?.id] });
    } catch (error) {
      console.error("[Discover] Error submitting review:", error);
      console.error("[Discover] Error details:", {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      hapticError();
      const errorMsg = error.message || "Failed to submit review";
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveTrack = async () => {
    if (!currentUser || !currentTrack) return;
    
    hapticMedium();
    
    try {
      if (isSaved) {
        // Unsave - use track_likes table as saved tracks
        const { error } = await supabase
          .from("track_likes")
          .delete()
          .eq("track_id", currentTrack.id)
          .eq("user_id", currentUser.id);
        
        if (error) throw error;
        
        setIsSaved(false);
        hapticSuccess();
        toast.success("Track unsaved");
      } else {
        // Save - use track_likes table
        const { error } = await supabase
          .from("track_likes")
          .insert({
            track_id: currentTrack.id,
            user_id: currentUser.id
          });
        
        if (error) throw error;
        
        setIsSaved(true);
        hapticSuccess();
        toast.success("Track saved!");
      }
    } catch (error) {
      console.error("Error saving track:", error);
      hapticError();
      toast.error(error.message || "Failed to save track");
    }
  };

  const handlePraise = async () => {
    if (!currentUser) {
      hapticError();
      toast.error("Please log in to vote");
      return;
    }
    
    if (!currentTrack || hasPraised || hasScathed) {
      console.log("handlePraise blocked:", { currentUser: !!currentUser, currentTrack: !!currentTrack, hasPraised, hasScathed });
      return;
    }
    
    // Require 50% play progress before allowing praise (admins bypass)
    console.log("[Discover] Praise check - isAdmin:", isAdmin, "progress:", progress);
    if (progress < 50 && !isAdmin) {
      hapticError();
      toast.error("Listen to at least 50% of the track before voting");
      return;
    }
    
    console.log("handlePraise called for track:", currentTrack.id);
    hapticMedium();
    
    try {
      // Insert vote record to Supabase
      console.log("Attempting to insert vote record to Supabase...");
      const { data: voteData, error: voteError } = await supabase
        .from('votes')
        .insert({
          track_id: currentTrack.id,
          voter_id: currentUser.id,
          vote_type: 'praise'
        })
        .select()
        .single();

      if (voteError) {
        // Check if it's a duplicate error
        if (voteError.code === '23505') {
          console.log("User already voted for this track");
          setHasPraised(true);
          hapticSuccess();
          toast.success("You've already praised this track! ❤️");
          return;
        }
        console.error("Unexpected vote insert error:", voteError);
        throw new Error(voteError.message || "Failed to create vote");
      }

      console.log("Vote record inserted successfully:", voteData);

      // Increment praise_count directly in Supabase
      const { data: trackData } = await supabase
        .from('tracks')
        .select('praise_count')
        .eq('id', currentTrack.id)
        .single();
      
      const { error: updateError } = await supabase
        .from('tracks')
        .update({ praise_count: (trackData?.praise_count || 0) + 1 })
        .eq('id', currentTrack.id);

      if (updateError) {
        console.error("Error updating praise count:", updateError);
      } else {
        console.log("Successfully incremented praise_count");
      }

      setHasPraised(true);
      hapticSuccess();
      toast.success("Praised! ❤️");
    } catch (error) {
      console.error("Error praising track:", error);
      hapticError();
      toast.error("Failed to praise track: " + (error.message || "Unknown error"));
    }
  };

  const handleScathe = async () => {
    if (!currentUser) {
      hapticError();
      toast.error("Please log in to vote");
      return;
    }
    
    if (!currentTrack || hasScathed || hasPraised) return;
    
    // Require 50% play progress before allowing scathe (admins bypass)
    console.log("[Discover] Scathe check - isAdmin:", isAdmin, "progress:", progress);
    if (progress < 50 && !isAdmin) {
      hapticError();
      toast.error("Listen to at least 50% of the track before voting");
      return;
    }
    
    hapticMedium();
    
    try {
      // Insert vote record to Supabase
      const { data: voteData, error: voteError } = await supabase
        .from('votes')
        .insert({
          track_id: currentTrack.id,
          voter_id: currentUser.id,
          vote_type: 'scathe'
        })
        .select()
        .single();

      if (voteError) {
        // Check if it's a duplicate error
        if (voteError.code === '23505') {
          console.log("User already voted scathe for this track");
          setHasScathed(true);
          hapticSuccess();
          toast.info("You've already scathed this track!");
          return;
        }
        console.error("Unexpected vote insert error:", voteError);
        throw new Error(voteError.message || "Failed to create vote");
      }
      
      // Decrement praise_count (min 1) and increment scathe_count
      const { data: trackData } = await supabase
        .from('tracks')
        .select('praise_count, scathe_count')
        .eq('id', currentTrack.id)
        .single();
      
      const currentPraise = trackData?.praise_count || 1;
      const newPraise = Math.max(1, currentPraise - 1);
      const wasted = currentPraise <= 1;
      
      await supabase
        .from('tracks')
        .update({ 
          praise_count: newPraise,
          scathe_count: (trackData?.scathe_count || 0) + 1 
        })
        .eq('id', currentTrack.id);

      setHasScathed(true);
      hapticSuccess();
      
      if (wasted) {
        toast.info("Track already at minimum votes - your downvote had no effect");
      } else {
        toast.success("Downvoted! -1 vote removed from leaderboard");
      }
    } catch (error) {
      console.error("Error downvoting track:", error);
      hapticError();
      toast.error("Failed to downvote track");
    }
  };

  useEffect(() => {
    // Reset feedback form when track changes
    setFeedbackText("");
    setHasPraised(false);
    setHasScathed(false);
    setTrackCompleted(false);
    setReviewDisabled(false);
    lastProgressMilestoneRef.current = 0; // Reset progress tracking
    lastProgressUpdateTimeRef.current = 0; // Reset timestamp
    
    // Check if track is saved (using track_likes table)
    const checkSaved = async () => {
      if (playerCurrentTrack?.id && currentUser?.id) {
        try {
          const { data } = await supabase
            .from("track_likes")
            .select("id")
            .eq("track_id", playerCurrentTrack.id)
            .eq("user_id", currentUser.id)
            .maybeSingle();
          setIsSaved(!!data);
        } catch (error) {
          console.error("Error checking saved status:", error);
          setIsSaved(false);
        }
      } else {
        setIsSaved(false);
      }
    };

    // Check if user has already reviewed (using feedback table)
    const checkReviewed = async () => {
      if (playerCurrentTrack?.id && currentUser?.id) {
        try {
          const { data } = await supabase
            .from("feedback")
            .select("id")
            .eq("track_id", playerCurrentTrack.id)
            .eq("reviewer_id", currentUser.id)
            .maybeSingle();
          setHasReviewed(!!data);
        } catch (error) {
          console.error("Error checking review status:", error);
          setHasReviewed(false);
        }
      } else {
        setHasReviewed(false);
      }
    };
    
    // Fetch bio for current track's artist (has_winner_badge doesn't exist in table)
    const fetchArtistBadge = async () => {
      if (playerCurrentTrack?.artist_id) {
        try {
          const { data: profile } = await supabase
            .from("profiles")
            .select("bio")
            .eq("id", playerCurrentTrack.artist_id)
            .single();
          setArtistBadgeStatus(false); // Column doesn't exist
          setArtistBio(profile?.bio || null);
        } catch (error) {
          console.error("Error fetching artist profile:", error);
          setArtistBadgeStatus(false);
          setArtistBio(null);
        }
      } else {
        setArtistBadgeStatus(false);
        setArtistBio(null);
      }
    };
    
    checkSaved();
    checkReviewed();
    fetchArtistBadge();
  }, [playerCurrentTrack, currentUser]);

  if (isLoading) {
    return (
      <div className="min-h-screen px-4 py-4 pb-32">
        <div className="max-w-sm mx-auto">
          <div className="text-center mb-4">
            <h1 className="text-2xl md:text-3xl font-light text-[#d0d0d0]">Discover</h1>
          </div>
          <DiscoverSkeleton />
        </div>
      </div>
    );
  }

  if (!currentTrack) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-center">
          {isError ? (
            <>
              <p className="text-[#808080] mb-4">Failed to load tracks</p>
              <Button
                onClick={() => refetch()}
                className="neuro-base active:neuro-pressed rounded-2xl px-6 py-3 mr-2"
                tooltip="Try loading tracks again"
              >
                <span className="text-[#a0a0a0]">Retry</span>
              </Button>
            </>
          ) : (
            <>
              <p className="text-[#808080] mb-4">No tracks available</p>
              <Button
                onClick={() => navigate(createPageUrl("Upload"))}
                className="neuro-base active:neuro-pressed rounded-2xl px-6 py-3"
                tooltip="Upload your own track to Soundope"
              >
                <span className="text-[#a0a0a0]">Upload a Track</span>
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  const wordCount = countWords(feedbackText);
  // Admins can leave feedback at any progress, others need 60%
  const canLeaveFeedback = isAdmin ? true : progress >= 60;
  // Can skip tracks anytime (review form is always visible)
  const canSkip = true;

  return (
    <div className="min-h-screen px-4 py-4 pb-32 relative">
 
 
       <div className="max-w-sm mx-auto">

        <div className="neuro-base rounded-2xl p-3">
          <div className="text-center mb-2">
            <h1 className="text-2xl md:text-3xl font-light text-[#d0d0d0] mb-0.5">Discover</h1>
          </div>

          {/* Tabs for Cover and Artist Bio */}
          <Tabs defaultValue="cover" className="mb-2">
            <TabsList className="grid w-full grid-cols-2 neuro-base rounded-xl p-1 mb-2">
              <TabsTrigger value="cover" className="text-xs data-[state=active]:neuro-pressed rounded-lg">
                Cover
              </TabsTrigger>
              <TabsTrigger value="artist" className="text-xs data-[state=active]:neuro-pressed rounded-lg">
                Artist
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="cover" className="mt-0">
              <div className="neuro-pressed rounded-xl overflow-hidden">
                <img
                  src={currentTrack.cover_image_url || getDefaultCoverImage(currentTrack.id)}
                  alt={currentTrack.title}
                  className="w-full aspect-[3/4] object-cover"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = getDefaultCoverImage(currentTrack.id);
                  }}
                />
              </div>
            </TabsContent>
            
            <TabsContent value="artist" className="mt-0">
              <div className="neuro-pressed rounded-xl p-4 min-h-[300px]">
                {artistBio ? (
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-[#d0d0d0] mb-2">About {currentTrack.artist_name}</h3>
                    <p className="text-xs text-[#a0a0a0] leading-relaxed whitespace-pre-wrap">
                      {artistBio}
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full min-h-[300px]">
                    <p className="text-xs text-[#707070] text-center">
                      {currentTrack.artist_name} hasn&apos;t added a bio yet
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>

          <div className="text-center mb-2">
            <h2 className="text-lg md:text-xl font-light text-[#d0d0d0] mb-0.5">
              {currentTrack.title}
            </h2>
            <div className="flex items-center justify-center gap-2 mb-1">
              <p className="text-sm md:text-base text-[#909090] flex items-center gap-1.5">
                {currentTrack.artist_name}
                {artistBadgeStatus && <WinnerBadge />}
                {currentTrack.artist_id && <ArtistLeaderboardBadge artistId={currentTrack.artist_id} />}
              </p>
              {currentTrack.artist_id && currentUser && currentTrack.artist_id !== currentUser.id && (
                <SendMessageButton
                  recipientId={currentTrack.artist_id}
                  recipientName={currentTrack.artist_name}
                  currentUser={currentUser}
                  trackTitle={currentTrack.title}
                />
              )}
              <TooltipButton
                variant="outline"
                size="sm"
                onClick={() => setIsShareDialogOpen(true)}
                className="neuro-base active:neuro-pressed rounded-xl p-1.5 h-auto"
                tooltip="Share this track"
              >
                <Share2 className="h-4 w-4 text-[#808080]" />
              </TooltipButton>
            </div>
            
            {currentTrack.genres && currentTrack.genres.length > 0 && (
              <div className="flex justify-center gap-1 mb-1">
                {currentTrack.genres.slice(0, 3).map((genre, idx) => (
                  <span
                    key={idx}
                    className="text-xs px-1.5 py-0.5 rounded-lg bg-[#1a1a1a] text-[#808080]"
                  >
                    {genre}
                  </span>
                ))}
              </div>
            )}

            {/* Track Description */}
            {currentTrack.description && (
              <div className="mt-2 neuro-pressed rounded-xl p-2">
                <p className="text-xs text-[#a0a0a0] leading-relaxed">
                  {currentTrack.description}
                </p>
              </div>
            )}
          </div>

          {/* Playback and voting controls */}
          <div className="mb-2">
            <div className="flex flex-col items-center justify-center gap-1.5">
              <div className="flex items-center justify-center gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={handleScathe}
                        disabled={hasScathed || hasPraised || (!isAdmin && (isNaN(progress) || progress < 50))}
                        className="p-1 hover:scale-110 transition-transform disabled:opacity-50 disabled:cursor-not-allowed bg-transparent border-none shadow-none hover:bg-transparent hover:drop-shadow-[0_0_8px_rgba(168,85,247,0.6)] outline-none focus:outline-none"
                        style={{ background: 'transparent', border: 'none', boxShadow: 'none' }}
                      >
                        <img
                          src={btnBad}
                          alt="Bad"
                          className="w-14 h-auto object-contain"
                        />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{hasScathed ? "Already scathed this track" : hasPraised ? "You already praised this track" : (!isAdmin && (isNaN(progress) || progress < 50)) ? "Listen to 50% to vote" : "Vote this track bad"}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipButton
                  onClick={skipToPrevious}
                  disabled={!canSkip}
                  className="neuro-flat active:neuro-pressed rounded-xl flex items-center justify-center p-0 disabled:opacity-50 disabled:cursor-not-allowed hover:drop-shadow-[0_0_8px_rgba(168,85,247,0.6)] transition-all"
                  tooltip={canSkip ? "Play previous track" : "Finish your review to skip tracks"}
                >
                  <img src={crowSkullLeft} alt="Previous" className="w-12 h-12 object-contain" />
                </TooltipButton>

                <TooltipButton
                  onClick={togglePlayPause}
                  className="neuro-flat active:neuro-pressed rounded-xl flex items-center justify-center p-0 hover:drop-shadow-[0_0_8px_rgba(168,85,247,0.6)] transition-all"
                  tooltip={isPlaying ? "Pause playback" : "Play track"}
                >
                  <img src={btnPlay} alt={isPlaying ? "Pause" : "Play"} className="w-12 h-12 object-contain" />
                </TooltipButton>
                
                <TooltipButton
                  onClick={skipToNext}
                  disabled={!canSkip}
                  className="neuro-flat active:neuro-pressed rounded-xl flex items-center justify-center p-0 disabled:opacity-50 disabled:cursor-not-allowed hover:drop-shadow-[0_0_8px_rgba(168,85,247,0.6)] transition-all"
                  tooltip={canSkip ? "Play next track" : "Finish your review to skip tracks"}
                >
                  <img src={crowRight} alt="Next" className="w-12 h-12 object-contain" />
                </TooltipButton>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={handlePraise}
                        disabled={hasPraised || hasScathed || (!isAdmin && (isNaN(progress) || progress < 50))}
                        className="p-1 hover:scale-110 transition-transform disabled:opacity-50 disabled:cursor-not-allowed bg-transparent border-none shadow-none hover:bg-transparent hover:drop-shadow-[0_0_8px_rgba(168,85,247,0.6)] outline-none focus:outline-none"
                        style={{ background: 'transparent', border: 'none', boxShadow: 'none' }}
                      >
                        <img
                          src={btnGood}
                          alt="Good"
                          className="w-14 h-auto object-contain"
                        />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{hasPraised ? "Already praised this track" : hasScathed ? "You already scathed this track" : (!isAdmin && (isNaN(progress) || progress < 50)) ? "Listen to 50% to vote" : "Vote this track good"}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[0.6rem] text-[#808080]">Progress</span>
              <span className="text-[0.6rem] text-[#808080]">{Math.round(progress)}%</span>
            </div>
            <div className="relative">
              <Progress value={progress} className="h-1.5" />
              {/* Invisible range input overlay to allow scrubbing - admin only */}
              {isAdmin && (
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={0.5}
                  value={Number.isFinite(progress) ? progress : 0}
                  onChange={handleSeek}
                  className="absolute inset-0 w-full opacity-0 cursor-pointer"
                />
              )}
            </div>
            {!isAdmin && progress < 60 && !countdown && (
              <p className="text-[0.55rem] text-[#707070] mt-1 text-center">
                Listen to 60% to unlock feedback
              </p>
            )}
            {isAdmin && progress < 60 && !countdown && (
              <p className="text-[0.55rem] text-[#707070] mt-1 text-center">
                Admin: Feedback available at any progress
              </p>
            )}
            {progress >= 60 && progress < requiredCompletionThreshold && !isAdmin && !countdown && (
              <p className="text-[0.55rem] text-[#808080] mt-1 text-center">
                Listen to {requiredCompletionThreshold}% to submit review ({Math.round(progress)}% complete)
              </p>
            )}
            {(isAdmin || progress >= requiredCompletionThreshold) && !countdown && (
              <p className="text-[0.55rem] text-[#10B981] mt-1 text-center font-medium">
                ✓ You can submit your review now ({Math.round(progress)}% complete)
              </p>
            )}
            {trackCompleted && !countdown && (
              <p className="text-[0.55rem] text-[#10B981] mt-1 text-center font-medium">
                ✓ Track completed! You can submit your review now
              </p>
            )}
          </div>

          {/* Placeholder for countdown - actual countdown is fixed position below */}

          {hasReviewed && (
            <div className="w-full neuro-pressed rounded-xl py-1.5 mb-2 text-sm text-center">
              <span className="text-[#808080]">You&apos;ve already reviewed this track</span>
            </div>
          )}

          {/* Feedback Form - Always visible */}
          {!hasReviewed && (
            <div className="neuro-pressed rounded-xl p-2 mb-2">
              <h3 className="text-xs font-medium text-[#d0d0d0] mb-1.5 text-center">Write Your Review</h3>
              
              {/* Text Input - Always enabled, no typing restrictions */}
              <div className="mb-1.5">
                <Textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  disabled={false}
                  placeholder="Write genuine feedback about the track... What works? What could improve? (20+ words, 12+ unique words)"
                  className="neuro-base min-h-[60px] resize-none text-[#d0d0d0] placeholder:text-[#606060] text-sm md:text-base"
                />
                <div className="flex justify-between items-center mt-1">
                  <span className={`text-xs ${wordCount >= 20 ? "text-[#10B981]" : wordCount >= 1 ? "text-[#808080]" : "text-[#707070]"}`}>
                    {wordCount} / 20 words minimum
                  </span>
                  {wordCount >= 20 && (
                    <span className="text-xs text-[#10B981] font-medium">✓ Ready to submit</span>
                  )}
                  {wordCount < 20 && wordCount > 0 && (
                    <span className="text-xs text-[#808080]">Write {20 - wordCount} more words</span>
                  )}
                </div>
              </div>

              {/* Submit Button - Unlocks when review is ready */}
              <div className="flex gap-1.5">
                <TooltipButton
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log("[Discover] Submit button clicked", { wordCount, rating, progress, isSubmitting, isAdmin });
                    const canSubmit = !isSubmitting && wordCount >= 20 && rating > 0 && (isAdmin || progress >= 70);
                    if (canSubmit) {
                      handleSubmitFeedback();
                    } else {
                      console.log("[Discover] Button click ignored - conditions not met", {
                        isSubmitting,
                        wordCount,
                        rating,
                        progress,
                        isAdmin,
                      });
                    }
                  }}
                  disabled={
                    isSubmitting ||
                    wordCount < 20 ||
                    rating < 1 ||
                    (!isAdmin && progress < 70)
                  }
                  className={`w-full rounded-xl py-1.5 text-xs smooth-transition ${
                    wordCount >= 20 &&
                    rating > 0 &&
                    (isAdmin || progress >= 70) &&
                    !isSubmitting
                      ? "neuro-base active:neuro-pressed hover:scale-105 text-[#d0d0d0] font-medium"
                      : "neuro-pressed opacity-50 cursor-not-allowed text-[#808080]"
                  }`}
                  tooltip={
                    rating < 1
                      ? "Please rate the track first"
                      : wordCount < 20
                        ? `Review must be at least 20 words (${wordCount}/20)`
                        : !isAdmin && progress < 70
                          ? `Listen to at least 70% to submit (${Math.round(progress)}% complete)`
                          : "Submit your review and earn $0.01 credit!"
                  }
                >
                  <span>
                    {isSubmitting
                      ? "Submitting..."
                      : rating < 1
                        ? "Rate the track first"
                        : wordCount < 20
                          ? `Need ${20 - wordCount} more words`
                          : !isAdmin && progress < 70
                            ? `Listen ${70 - Math.round(progress)}% more`
                            : "✓ Submit Review & Earn $0.01"}
                  </span>
                </TooltipButton>
              </div>
            </div>
          )}


        </div>

      </div>

      {/* Share Dialog */}
      <ShareDialog
        isOpen={isShareDialogOpen}
        onOpenChange={setIsShareDialogOpen}
        trackId={currentTrack?.id}
        trackTitle={currentTrack?.title}
        artistName={currentTrack?.artist_name}
      />

      {/* Fixed Position Countdown Timer - Bottom Right */}
      {countdown !== null && (
        <div
          onClick={cancelCountdown}
          className="fixed bottom-20 right-4 z-50 cursor-pointer transition-all hover:scale-105 active:scale-95"
          title="Click to cancel auto-skip"
        >
          <div className="neuro-base rounded-2xl p-4 shadow-2xl border border-primary/30 min-w-[120px]">
            <div className="text-center">
              <p className="text-[0.65rem] text-[#808080] mb-1 uppercase tracking-wider">Next track in</p>
              <div className="text-3xl font-bold text-primary tabular-nums">{countdown}s</div>
              <p className="text-[0.55rem] text-[#606060] mt-1">Tap to cancel</p>
            </div>
            {/* Progress ring */}
            <div className="mt-2 flex justify-center">
              <svg className="w-8 h-8 -rotate-90">
                <circle
                  cx="16"
                  cy="16"
                  r="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-[#303030]"
                />
                <circle
                  cx="16"
                  cy="16"
                  r="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeDasharray={2 * Math.PI * 14}
                  strokeDashoffset={2 * Math.PI * 14 * (1 - countdown / 13)}
                  className="text-primary transition-all duration-1000"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
